<template>
  <q-dialog
    ref="dialogRef"
    transition-duration="0"
    position="top"
    @hide="onDialogHide"
  >
    <div
      class="mx-auto w-full mt-24 max-w-xl max-h-[80vh] transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all"
    >
      <FocusTrap>
        <div class="w-full flex flex-col">
          <div class="relative border-b border-gray-100 px-4 pt-4">
            <q-icon
              size="sm"
              name="fa-light fa-search"
              class="pointer-events-none absolute left-8 top-7 text-gray-400"
            />
            <input
              v-model="search"
              type="text"
              :placeholder="$t('conversations.assignDialog.inputPlaceholder')"
              class="h-12 w-full border-0 outline-none bg-transparent pl-14 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
            />
          </div>

          <!-- Lists in a row -->
          <div class="flex flex-row items-start gap-6 px-4 pt-4">
            <!-- Team List -->
            <div class="flex-1">
              <div class="font-semibold text-gray-700 mb-2">
                {{ $t("conversations.assignDialog.selectTeam") }}
              </div>
              <div class="overflow-y-auto max-h-[40vh] pr-2">
                <AssignList
                  :items="teamListWithUnassigned"
                  :selected-id="selectedTeamId"
                  :on-select="(id: string | null) => selectedTeamId = id"
                />
              </div>
            </div>

            <!-- Agent List -->
            <div class="flex-1">
              <div class="font-semibold text-gray-700 mb-2">
                {{ $t("conversations.assignDialog.selectAgent") }}
              </div>
              <div class="overflow-y-auto max-h-[40vh] pr-2">
                <AssignList
                  :items="filteredAgentsWithUnassigned"
                  :selected-id="selectedAgentId"
                  :on-select="(id: string | null) => selectedAgentId = id"
                  mode="agent"
                />
              </div>
            </div>
          </div>

          <!-- Button and selected info -->
          <div class="px-4 pb-4 pt-2 flex justify-end">
            <div class="px-4 pt-2 text-sm text-gray-600">
              <span v-if="selectedTeamId">
                Team: <strong>{{ getTeamName(selectedTeamId) }}</strong>
              </span>
              <span v-if="selectedTeamId && selectedAgentId">
                &nbsp;&nbsp;|&nbsp;&nbsp;
              </span>
              <span v-if="selectedAgentId">
                Agent: <strong>{{ getAgentName(selectedAgentId) }}</strong>
              </span>
            </div>
            <q-btn
              :label="$t('conversations.assignDialog.assignConversation')"
              color="primary"
              :disable="isSaving"
              @click="onSelect"
            />
          </div>
        </div>
      </FocusTrap>
    </div>
  </q-dialog>
</template>


<script setup lang="ts">
import {ref, PropType, computed, onMounted, watch} from "vue";
import {useShortcut} from "src/composables/useShortcut";
import {useI18n} from "vue-i18n";
import {teamListGlobalStore} from "src/stores/team-list-global-store";
import {conversationAssignStore} from "src/features/conversation/conversation-assign/conversation-assign-global-store";
import {TeamOrAgentType} from "src/shared/models/models";
import {conversationAssignCommand} from "src/features/conversation/conversation-assign/api-commands";
import {useQuasar, useDialogPluginComponent} from "quasar";
import {FocusTrap} from "@headlessui/vue";
import AssignList from "src/features/conversation/assign-list/AssignList.vue";
import type { ConversationAssignItem } from "src/features/conversation/conversation-assign/conversation-assign-global-store";

const props = defineProps({
  conversationIds: {
    type: Array as PropType<string[]>,
    required: true,
  },

  currentAgentId: {
    type: String,
    required: false,
    default: () => null,
  },

  currentTeamId: {
    type: String,
    required: false,
    default: () => null,
  },
});

onMounted(() => {
  conversationAssignStore.updateFromTeamList();
});


const selectedTeamId = ref<string | null>(props.currentTeamId || null);
const selectedAgentId = ref<string | null>(props.currentAgentId || null);

function getAgentName(id: string | null) {
  return conversationAssignStore.getList().find(i => i.id === id)?.label || "";
}

function getTeamName(id: string | null) {
  return teamListGlobalStore.getList().find(t => t.id === id)?.name || "";
}

watch([selectedTeamId, selectedAgentId], () => {
  console.log("Selected Team ID:", selectedTeamId.value);
  console.log("Selected Agent ID:", selectedAgentId.value);
});

const teamListWithUnassigned = computed(() => {
  const filtered = teamListGlobalStore.getList()
    .filter(team => team.name.toLowerCase().includes(search.value.toLowerCase()))
    .map((team, index) => ({
      id: team.id,
      label: team.name,
      type: TeamOrAgentType.Team,
      ui: {
        index,
        selected: false,
        hover: false,
        key: index,
      },
    }));

  return [{
    id: null,
    label: "Unassigned",
    type: TeamOrAgentType.None,
    ui: {index: -1, selected: false, hover: false, key: -1}
  }, ...filtered];
});

const filteredAgentsWithUnassigned = computed(() => {
  let agents: ConversationAssignItem[] = [];

  if (selectedTeamId.value === null) {
    const seen = new Set<string>();
    agents = conversationAssignStore.getAllAgents().filter(agent => {
      if (seen.has(agent.id)) return false;
      seen.add(agent.id);
      return true;
    });
  } else {
    const team = teamListGlobalStore.getList().find(t => t.id === selectedTeamId.value);
    agents = team?.agents.map((a, i) => ({
      id: a.id,
      label: a.name,
      type: TeamOrAgentType.Agent,
      ui: {
        index: i,
        selected: false,
        hover: false,
        key: i,
      },
    })) ?? [];
  }

  const filtered = agents
    .filter(agent =>
      agent.label.toLowerCase().includes(search.value.toLowerCase())
    );

  return [
    {
      id: null,
      label: "Unassigned",
      type: TeamOrAgentType.None,
      ui: {index: -1, selected: false, hover: false, key: -1}
    },
    ...filtered
  ];
});

watch(selectedTeamId, newTeamId => {
  if (!selectedAgentId.value) return;

  if (newTeamId === null) {
    return;
  }

  const selectedTeam = teamListGlobalStore.getList().find(t => t.id === newTeamId);

  const agentStillInTeam = selectedTeam?.agents.some(agent => agent.id === selectedAgentId.value);

  if (!agentStillInTeam) {
    selectedAgentId.value = null;
  }
});


const {dialogRef, onDialogOK, onDialogHide} = useDialogPluginComponent();
defineEmits([...useDialogPluginComponent.emits]);

const {t} = useI18n();
const $q = useQuasar();

const search = ref("");
const isSaving = ref(false);

async function onSelect() {
  if (isSaving.value) {
    console.log("Assignment already in progress");
    return;
  }

  isSaving.value = true;

  const progress = $q.notify({
    type: "ongoing",
    message: t("conversation.actions.assign.assignProgressMessage"),
  });

  for (const conversationId of props.conversationIds) {
    try {
      const agentId = selectedAgentId.value;
      const teamId = selectedTeamId.value;

      await conversationAssignCommand(
        conversationId,
        agentId,
        teamId
      );

      progress({
        type: "positive",
        message: t("conversation.actions.assign.successToastMessage"),
        actions: [
          {
            label: t("common.undo"),
            color: "yellow",
            handler: async () => {
              await conversationAssignCommand(conversationId, props.currentAgentId, props.currentTeamId);
            },
          },
        ],
      });

      onDialogOK();
    } catch (err) {
      progress({
        message: t("conversations.assignDialog.couldNotAssignConversationTitle"),
        type: "negative",
      });
    } finally {
      isSaving.value = false;
    }
  }
}


useShortcut("conversationAssign", [], {
  newShortcutStack: true,
});
</script>

<style lang="sass" scoped></style>
